/**
 * Octane AI Quiz Product Display Handler
 * Optimized for performance and maintainability
 * 
 * Features:
 * - Auto-selects recommended scent variant based on quiz answers
 * - Auto-selects one-time purchase option
 * - Displays product reviews and subtitles
 * - Dynamic price updates based on variant selection
 */

(function() {
    'use strict';

    // Configuration
    const CONFIG = {
        shopDomain: 'theearthlingco.com',
        cartAddUrl: 'https://theearthlingco.com/cart/add',
        debug: true // Set to false in production
    };

    // Utility functions
    const utils = {
        log: function(...args) {
            if (CONFIG.debug) {
                console.log(...args);
            }
        },
        
        logSection: function(title, data) {
            if (CONFIG.debug) {
                console.log('========== ' + title + ' ==========');
                console.log(data);
                if (typeof data === 'object') {
                    console.log('JSON:', JSON.stringify(data, null, 2));
                }
            }
        },

        extractVariantFromUrl: function(url) {
            if (!url || !url.includes('?variant')) return null;
            try {
                const urlParams = new URLSearchParams(url.split('?')[1]);
                return urlParams.get('variant');
            } catch (e) {
                const match = url.match(/[?&]variant=([^&]+)/);
                return match ? match[1] : null;
            }
        },

        extractQuizAnswer: function(answers, key) {
            if (!answers || typeof answers !== 'object') return null;
            
            // Direct key match
            if (answers[key] !== undefined) return answers[key];
            if (answers[key.toLowerCase()] !== undefined) return answers[key.toLowerCase()];
            
            // Case-insensitive search
            for (var prop in answers) {
                if (prop.toLowerCase() === key.toLowerCase()) {
                    return answers[prop];
                }
            }
            
            // Partial match search
            const lowerKey = key.toLowerCase();
            for (var prop in answers) {
                if (prop.toLowerCase().includes(lowerKey) || lowerKey.includes(prop.toLowerCase())) {
                    return answers[prop];
                }
            }
            
            return null;
        },

        findVariantOption: function(options, searchValue, searchType) {
            if (!options || options.length === 0) return null;
            
            const searchStr = String(searchValue).toLowerCase().trim();
            const optionsArray = Array.from(options);
            
            for (var i = 0; i < optionsArray.length; i++) {
                var opt = optionsArray[i];
                
                if (searchType === 'id') {
                    if (String(opt.value) === String(searchValue)) {
                        return opt;
                    }
                } else if (searchType === 'name') {
                    const optionText = opt.text.toLowerCase().trim();
                    // More flexible matching - check if search term is contained in option text
                    // or if option text is contained in search term
                    if (optionText === searchStr) {
                        return opt; // Exact match
                    }
                    // Check if search term words are all present in option text
                    var searchWords = searchStr.split(/\s+/);
                    var allWordsMatch = searchWords.every(function(word) {
                        return optionText.includes(word);
                    });
                    if (allWordsMatch && searchWords.length > 0) {
                        return opt;
                    }
                    // Also check reverse (option text words in search term)
                    var optionWords = optionText.split(/\s+/);
                    var allOptionWordsMatch = optionWords.every(function(word) {
                        return searchStr.includes(word);
                    });
                    if (allOptionWordsMatch && optionWords.length > 0) {
                        return opt;
                    }
                }
            }
            
            return null;
        },

        formatPrice: function(cents) {
            return (cents / 100).toFixed(2);
        },

        formatReviewCount: function(count) {
            if (!count) return '0';
            if (count >= 1000) {
                const thousands = Math.floor(count / 1000);
                const remainder = count % 1000;
                return thousands + ',' + String(remainder).padStart(3, '0');
            }
            return String(count);
        },

        getRatingPercentage: function(rating) {
            if (rating >= 4.5) return 100;
            if (rating >= 4.0) return 90;
            return 80;
        }
    };

    // Extract recommended variant/scent from Octane AI product object
    function extractRecommendedVariant(product) {
        var recommendedVariantId = product.variant_id || product.variantId || null;
        var recommendedScent = product.scent || product.variant_title || product.variant || null;
        
        // Extract variant from product title (e.g., "Citrus Sun Shampoo..." -> "Citrus Sun")
        if (product.title && !recommendedScent) {
            // Common patterns: "Citrus Sun Shampoo...", "Simple Fresh Shampoo..."
            var titleParts = product.title.split(' ');
            if (titleParts.length >= 2) {
                // Check if first two words match a variant name
                var possibleScent = titleParts[0] + ' ' + titleParts[1];
                recommendedScent = possibleScent;
            } else if (titleParts.length === 1) {
                recommendedScent = titleParts[0];
            }
        }
        
        // Check product URL for variant
        if (product.link) {
            const urlVariantId = utils.extractVariantFromUrl(product.link);
            if (urlVariantId && !recommendedVariantId) {
                recommendedVariantId = urlVariantId;
            }
        }
        
        // Check custom properties
        if (product.properties && typeof product.properties === 'object') {
            for (var prop in product.properties) {
                if (prop.toLowerCase().includes('scent') || prop.toLowerCase().includes('variant')) {
                    recommendedScent = product.properties[prop];
                    break;
                }
            }
        }
        
        return {
            variantId: recommendedVariantId,
            scent: recommendedScent
        };
    }

    // Auto-select variant based on priority
    function autoSelectVariant(selectElement, recommended, vacationAnswer, variantFromUrl, firstAvailableVariantId, template) {
        if (!selectElement || selectElement.options.length === 0) return null;
        
        var selectedOption = null;
        var selectedVariantId = null;
        var priorityUsed = null;
        
        // Priority 1: Recommended variant ID from Octane AI
        if (recommended.variantId) {
            selectedOption = utils.findVariantOption(selectElement.options, recommended.variantId, 'id');
            if (selectedOption) {
                selectedVariantId = recommended.variantId;
                priorityUsed = 1;
                utils.log('✓ Selected variant from Octane AI variant ID:', recommended.variantId);
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 2: Recommended scent name match (from title or Octane AI)
        if (recommended.scent) {
            selectedOption = utils.findVariantOption(selectElement.options, recommended.scent, 'name');
            if (selectedOption) {
                selectedVariantId = selectedOption.value;
                priorityUsed = 2;
                utils.log('✓ Selected variant from recommended scent:', recommended.scent);
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 3: Vacation answer match
        if (vacationAnswer) {
            selectedOption = utils.findVariantOption(selectElement.options, vacationAnswer, 'name');
            if (selectedOption) {
                selectedVariantId = selectedOption.value;
                priorityUsed = 3;
                utils.log('✓ Selected variant from vacation answer:', vacationAnswer);
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 4: Variant from URL parameter
        if (variantFromUrl) {
            selectedOption = utils.findVariantOption(selectElement.options, variantFromUrl, 'id');
            if (selectedOption) {
                selectedVariantId = variantFromUrl;
                priorityUsed = 4;
                utils.log('✓ Selected variant from URL parameter:', variantFromUrl);
                return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
            }
        }
        
        // Priority 5: First available variant (fallback)
        if (selectElement.options.length > 0) {
            selectedOption = selectElement.options[0];
            selectedVariantId = selectedOption.value;
            priorityUsed = 5;
            utils.log('✓ Selected first available variant:', selectedVariantId);
            return { option: selectedOption, variantId: selectedVariantId, priorityUsed: priorityUsed };
        }
        
        return null;
    }

    // Get product metafields from Liquid-rendered hidden elements
    function getProductMetafields(productHandle) {
        var metafieldEl = document.querySelector('.quiz-product-metafield.' + productHandle);
        if (!metafieldEl) {
            utils.log('Metafield element not found for:', productHandle);
            return null;
        }
        
        var ratingEl = metafieldEl.querySelector('.rating-average');
        var countEl = metafieldEl.querySelector('.rating-count');
        var subtitleEl = metafieldEl.querySelector('.cp-subtitle');
        
        var metafields = {
            rating: null,
            ratingCount: null,
            ratingPercentage: null,
            subtitle: null
        };
        
        if (ratingEl) {
            metafields.rating = parseFloat(ratingEl.getAttribute('data-rating'));
            metafields.ratingPercentage = parseInt(ratingEl.getAttribute('data-rating-percentage'));
        }
        
        if (countEl) {
            metafields.ratingCount = parseInt(countEl.getAttribute('data-count'));
        }
        
        if (subtitleEl) {
            metafields.subtitle = subtitleEl.getAttribute('data-subtitle');
        }
        
        return metafields;
    }

    // Update product display with reviews - get from Liquid-rendered metafields
    function updateProductReviews(template, productHandle, productUrl) {
        var ratingStarsEl = template.querySelector(".loop-rating-row .native_rating_stars");
        var ratingTextEl = template.querySelector(".loop-rating-row .rating-text");
        var ratingRow = ratingStarsEl ? ratingStarsEl.parentElement : null;
        
        if (!ratingStarsEl) {
            utils.log('Rating stars element not found');
            if (ratingRow) ratingRow.style.display = "none";
            return;
        }
        
        // Get metafields from Liquid-rendered hidden elements
        var metafields = getProductMetafields(productHandle);
        
        if (!metafields) {
            utils.log('No metafields found for product:', productHandle);
            if (ratingRow) ratingRow.style.display = "none";
            return;
        }
        
        utils.log('Metafields for', productHandle + ':', metafields);
        
        var rating = metafields.rating;
        var ratingCount = metafields.ratingCount;
        var ratingPercentage = metafields.ratingPercentage;
        
        if (rating && ratingPercentage !== null) {
            ratingStarsEl.className = "native_rating_stars stars_num_" + ratingPercentage;
            ratingStarsEl.setAttribute("aria-label", "Rating of this product is " + rating + " out of 5.");
            
            if (ratingTextEl) {
                // Just show the rating number, no "/5" or review count
                ratingTextEl.innerText = rating.toString();
            }
            if (ratingRow) {
                ratingRow.style.display = "flex";
                utils.log('Rating row displayed with rating:', rating);
            }
        } else {
            utils.log('No rating data found, hiding rating row');
            if (ratingRow) ratingRow.style.display = "none";
        }
    }

    // Update product subtitle - get from Liquid-rendered metafields
    function updateProductSubtitle(template, productHandle, productUrl) {
        var subtitleEl = template.querySelector(".product-subtitle");
        if (!subtitleEl) {
            utils.log('Subtitle element not found');
            return;
        }
        
        // Get metafields from Liquid-rendered hidden elements
        var metafields = getProductMetafields(productHandle);
        
        if (!metafields) {
            utils.log('No metafields found for product:', productHandle);
            subtitleEl.style.display = "none";
            return;
        }
        
        var subtitleText = metafields.subtitle;
        
        utils.log('Subtitle for', productHandle + ':', subtitleText);
        
        if (subtitleText && subtitleText.trim() !== '') {
            // Clean up "Read More >" if present and just show the subtitle text
            var cleanSubtitle = String(subtitleText).replace(/Read More\s*>?/gi, '').trim();
            subtitleEl.innerText = cleanSubtitle;
            subtitleEl.style.display = "block";
            utils.log('Subtitle displayed:', cleanSubtitle);
        } else {
            utils.log('No subtitle found, hiding element');
            subtitleEl.style.display = "none";
        }
    }

    // Update product price display
    function updateProductPrice(template, variant, fallbackPrice) {
        var priceElement = template.querySelector(".price--end .money");
        var priceWithoutDiscount = template.querySelector(".price--without-discount");
        var priceWithoutDiscountMoney = priceWithoutDiscount ? priceWithoutDiscount.querySelector(".money") : null;
        
        if (!priceElement) return;
        
        if (variant) {
            var variantPrice = utils.formatPrice(variant.price);
            priceElement.innerText = "$" + variantPrice;
            
            // Update aria-label based on whether there's a discount
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
                // Discounted price
                priceElement.setAttribute("aria-label", "Discounted price");
                
                // Show and set original price
                if (priceWithoutDiscount && priceWithoutDiscountMoney) {
                    var comparePrice = utils.formatPrice(variant.compare_at_price);
                    priceWithoutDiscountMoney.innerText = "$" + comparePrice;
                    priceWithoutDiscount.style.display = "inline";
                }
            } else {
                // Regular price
                priceElement.setAttribute("aria-label", "Price");
                
                // Hide original price
                if (priceWithoutDiscount) {
                    priceWithoutDiscount.style.display = "none";
                }
            }
        } else if (fallbackPrice) {
            priceElement.innerText = fallbackPrice;
            // Default to "Price" aria-label when using fallback
            priceElement.setAttribute("aria-label", "Price");
        }
    }

    // Process product variants and populate selectors
    function processProductVariants(product, selectVariant, indexAvailableArray) {
        var lowercasedImagesArray = product.images ? product.images.map(function(img) { return img.toLowerCase(); }) : [];
        var croppedImage = product.featured_image || (product.images && product.images[0]) || '';
        
        for (var i = 0; i < product.variants.length; i++) {
            if (!product.variants[i].available) continue;
            
            indexAvailableArray.push(i);
            
            var variantId = product.variants[i].id;
            var variantTitle = product.variants[i].title;
            
            if (product.variants.length > 1) {
                var opt = new Option(variantTitle, variantId);
                opt.setAttribute('data-variant-price', utils.formatPrice(product.variants[i].price));
                
                // Store compare_at_price if available
                if (product.variants[i].compare_at_price && product.variants[i].compare_at_price > product.variants[i].price) {
                    opt.setAttribute('data-variant-compare-price', utils.formatPrice(product.variants[i].compare_at_price));
                }
                
                // Set variant image
                var variantFeaturedImageSrc = product.variants[i].featured_image && product.variants[i].featured_image.src;
                if (variantFeaturedImageSrc) {
                    opt.setAttribute("data-varimg", variantFeaturedImageSrc);
                } else if (lowercasedImagesArray.length > 0) {
                    var handleizeImageTitle = variantTitle.includes('Poplar') 
                        ? variantTitle.replaceAll(' ', '').replaceAll('"', '').replaceAll('(', '').replaceAll(')', '').toLowerCase()
                        : variantTitle.replaceAll(' ', '-').replaceAll('"', '').replaceAll('(', '').replaceAll(')', '').replaceAll('-/-', '-').toLowerCase();
                    
                    var varImgIndex = lowercasedImagesArray.findIndex(function(element) {
                        return element.includes(handleizeImageTitle);
                    });
                    
                    if (varImgIndex !== -1) {
                        opt.setAttribute("data-varimg", product.images[varImgIndex]);
                        if (i === indexAvailableArray[0]) {
                            croppedImage = product.images[varImgIndex];
                        }
                    }
                }
                
                selectVariant.options[selectVariant.options.length] = opt;
                selectVariant.classList.remove('hidden');
            }
        }
        
        return croppedImage;
    }

    // Setup subscription selector
    function setupSubscriptionSelector(product, selectSubscr) {
        if (!product.selling_plan_groups || product.selling_plan_groups.length === 0) return;
        
        var sPlans = product.selling_plan_groups;
        var subGroup = sPlans.find(function(g) {
            var nameMatch = g.name && g.name.toLowerCase().includes('subscribe');
            var hasFrequency = Array.isArray(g.options) && g.options.some(function(o) { return o && o.name === 'Frequency'; });
            return nameMatch || hasFrequency;
        }) || sPlans[0];
        
        if (subGroup && subGroup.selling_plans && subGroup.selling_plans.length > 0) {
            selectSubscr.classList.remove("hidden");
            
            var oneTimeOption = document.createElement("option");
            oneTimeOption.value = "1";
            oneTimeOption.text = "One-time";
            selectSubscr.appendChild(oneTimeOption);
            
            subGroup.selling_plans.forEach(function(plan) {
                var spOpt = document.createElement("option");
                spOpt.value = plan.id;
                spOpt.text = plan.name;
                selectSubscr.appendChild(spOpt);
            });
            
            // Auto-select "One-time" purchase
            selectSubscr.value = "1";
        }
    }

    // Process a single product
    function processProduct(octaneProduct, index, template, vacationAnswer, productsContainer) {
        console.log('🟢 ========== PROCESSING PRODUCT [' + index + '] ==========');
        console.log('🟢 Octane Product:', octaneProduct);
        
        var productUrl = octaneProduct.link || '';
        var variantFromUrl = utils.extractVariantFromUrl(productUrl);
        var cleanProductUrl = productUrl.split('?')[0];
        
        console.log('🟢 Product URL:', productUrl);
        console.log('🟢 Clean Product URL:', cleanProductUrl);
        console.log('🟢 Variant from URL:', variantFromUrl);
        
        // Extract recommended variant info
        var recommended = extractRecommendedVariant(octaneProduct);
        console.log('🟢 Recommended variant:', recommended);
        
        // Extract image from Octane AI product object
        var octaneImage = octaneProduct.image || null;
        console.log('🟢 Octane image:', octaneImage);
        
        utils.logSection('PRODUCT [' + (index + 1) + '] DETAILS', {
            product: octaneProduct,
            recommended: recommended,
            variantFromUrl: variantFromUrl,
            productUrl: cleanProductUrl,
            octaneImage: octaneImage
        });
        
        var productHandle = cleanProductUrl.replace('https://' + CONFIG.shopDomain + '/products/', '');
        var fullUrl = cleanProductUrl + '.js';
        
        console.log('🟢 Product Handle:', productHandle);
        console.log('🟢 Fetching from:', fullUrl);
        
        var selectVariant = template.querySelector('.upsells_variants');
        var selectSubscr = template.querySelector('.subscriptions');
        
        console.log('🟢 Select variant element:', selectVariant);
        console.log('🟢 Select subscription element:', selectSubscr);
        
            // Fetch product data from Shopify
        jQuery.getJSON(fullUrl, function(shopifyProduct) {
            console.log('🟢 ✅ Shopify product fetched:', shopifyProduct);
            console.log('🟢 Product available?', shopifyProduct.available);
            console.log('🟢 Variants count:', shopifyProduct.variants ? shopifyProduct.variants.length : 0);
            // reserved-for-subs: stock is held for Loop renewals, never recommend it for purchase
            if (!shopifyProduct.available || (shopifyProduct.tags || []).indexOf('reserved-for-subs') > -1) {
                template.classList.add('hidden');
                return;
            }
            
            // Check if this is a product that should prioritize variant images
            var productId = String(shopifyProduct.id);
            var prioritizeVariantImage = productId === '7162093600947';
            
            var indexAvailableArray = [];
            var croppedImage = processProductVariants(shopifyProduct, selectVariant, indexAvailableArray);
            
            // Use Octane image as initial image if available (unless this product prioritizes variant images)
            if (octaneImage && !prioritizeVariantImage) {
                croppedImage = octaneImage;
                console.log('🟢 Using Octane image:', croppedImage);
            }
            
            if (indexAvailableArray.length === 0) {
                template.classList.add('hidden');
                return;
            }
            
            // Setup subscription selector
            setupSubscriptionSelector(shopifyProduct, selectSubscr);
            
            // Get first available variant (fallback)
            var firstAvailableVariantId = shopifyProduct.variants[indexAvailableArray[0]].id;
            
            // Check if recommended variant exists in product but is out of stock
            var recommendedVariantOutOfStock = false;
            
            if (recommended.variantId) {
                var recommendedVariantObj = shopifyProduct.variants.find(function(v) {
                    return String(v.id) === String(recommended.variantId);
                });
                
                if (recommendedVariantObj) {
                    // Check if this variant is NOT in the available variants list
                    var isAvailable = indexAvailableArray.some(function(idx) {
                        return String(shopifyProduct.variants[idx].id) === String(recommended.variantId);
                    });
                    if (!isAvailable) {
                        recommendedVariantOutOfStock = true;
                        utils.log('⚠ Recommended variant ID exists but is out of stock:', recommended.variantId);
                    }
                }
            } else if (recommended.scent) {
                // Check if recommended scent matches an unavailable variant
                var recommendedScentVariant = shopifyProduct.variants.find(function(v) {
                    return v.title && v.title.toLowerCase().indexOf(recommended.scent.toLowerCase()) !== -1;
                });
                
                if (recommendedScentVariant) {
                    var isAvailable = indexAvailableArray.some(function(idx) {
                        return String(shopifyProduct.variants[idx].id) === String(recommendedScentVariant.id);
                    });
                    if (!isAvailable) {
                        recommendedVariantOutOfStock = true;
                        utils.log('⚠ Recommended scent variant exists but is out of stock:', recommended.scent, 'Variant ID:', recommendedScentVariant.id);
                    }
                }
            }
            
            // Auto-select variant (only if recommended variant is available, otherwise don't select anything)
            var selectedVariant = null;
            var finalVariantId = null;
            
            if (!recommendedVariantOutOfStock) {
                selectedVariant = autoSelectVariant(
                    selectVariant, 
                    recommended, 
                    vacationAnswer, 
                    variantFromUrl, 
                    firstAvailableVariantId,
                    template
                );
                
                finalVariantId = selectedVariant ? selectedVariant.variantId : firstAvailableVariantId;
                
                // Apply variant selection
                if (selectedVariant && selectedVariant.option) {
                    selectVariant.value = selectedVariant.variantId;
                    var imgVarUrl = selectedVariant.option.getAttribute('data-varimg');
                    // For product ID 7162093600947, prioritize variant image; otherwise prioritize Octane image
                    if (prioritizeVariantImage && imgVarUrl) {
                        template.querySelector("img").src = imgVarUrl;
                        croppedImage = imgVarUrl;
                        console.log('🟢 Using variant image (prioritized for this product):', imgVarUrl);
                    } else if (octaneImage) {
                        template.querySelector("img").src = octaneImage;
                        croppedImage = octaneImage;
                        console.log('🟢 Using Octane image (prioritized over variant image):', octaneImage);
                    } else if (imgVarUrl) {
                        // Only use variant image if Octane image is not available
                        template.querySelector("img").src = imgVarUrl;
                        croppedImage = imgVarUrl;
                        console.log('🟢 Using variant image (Octane image not available):', imgVarUrl);
                    }
                } else if (selectVariant.options.length > 0) {
                    selectVariant.value = selectVariant.options[0].value;
                }
            } else {
                // Recommended variant is out of stock - don't select anything, disable button
                finalVariantId = null;
                selectVariant.value = '';
            }
            
            // Update product title - use Octane title or fallback to Shopify product title
            var productTitle = octaneProduct.title || shopifyProduct.title || '';
            
            // Update product information - prioritize based on product ID
            var imgElement = template.querySelector("img");
            if (imgElement) {
                // For product ID 7162093600947, variant image is already set; otherwise prioritize Octane image
                if (!prioritizeVariantImage && octaneImage) {
                    imgElement.src = octaneImage;
                    console.log('🟢 Final image set to Octane image:', octaneImage);
                } else {
                    imgElement.src = croppedImage;
                    console.log('🟢 Final image set to:', croppedImage);
                }
            }
            template.querySelector("img").alt = productTitle;
            template.querySelector(".title").innerText = productTitle;
            
            // Update product links
            var productLinks = template.querySelectorAll('.recommended-product-card a[itemprop="url"]');
            productLinks.forEach(function(link) {
                link.setAttribute("href", cleanProductUrl);
            });
            template.querySelector('.product-info__caption').setAttribute("href", cleanProductUrl);
            
            // Update price
            var selectedVariantData = null;
            if (finalVariantId) {
                selectedVariantData = shopifyProduct.variants.find(function(v) {
                    return String(v.id) === String(finalVariantId);
                });
            }
            updateProductPrice(template, selectedVariantData, octaneProduct.price_formatted || octaneProduct.ending_price_formatted);
            
            // Update add to cart button
            var addToCartBtn = template.querySelector(".upsell-addtocart");
            var addToCartText = addToCartBtn.querySelector("span");
            if (recommendedVariantOutOfStock || !finalVariantId) {
                // Show "Sold Out" and disable button
                if (addToCartText) {
                    addToCartText.innerText = "Sold Out";
                } else {
                    addToCartBtn.innerText = "Sold Out";
                }
                addToCartBtn.setAttribute("href", "#");
                addToCartBtn.setAttribute("data-variant", "");
                addToCartBtn.style.opacity = "0.5";
                addToCartBtn.style.cursor = "not-allowed";
                addToCartBtn.style.pointerEvents = "none";
                utils.log('⚠ Recommended variant out of stock, showing "Sold Out"');
            } else {
                // Normal add to cart functionality
                var linkVariant = CONFIG.cartAddUrl + "?id=" + finalVariantId + "&quantity=1";
                addToCartBtn.setAttribute("href", linkVariant);
                addToCartBtn.setAttribute("data-variant", finalVariantId);
                addToCartBtn.style.opacity = "";
                addToCartBtn.style.cursor = "";
                addToCartBtn.style.pointerEvents = "";
                // Reset button text if it was "Sold Out"
                if (addToCartText) {
                    if (addToCartText.innerText === "Sold Out") {
                        addToCartText.innerText = "Add to cart";
                    }
                } else if (addToCartBtn.innerText === "Sold Out") {
                    addToCartBtn.innerText = "Add to cart";
                }
            }
            
            template.classList.add(shopifyProduct.handle);
            
            console.log('🟢 Appending product to container. Index:', index);
            console.log('🟢 productsContainer element:', productsContainer);
            console.log('🟢 template element:', template);
            
            // Append to products container
            if (productsContainer) {
                productsContainer.appendChild(template);
                console.log('🟢 ✅ Product appended to products container. Children count:', productsContainer.children.length);
            }
            
            // Update reviews and subtitle AFTER template is in DOM
            // Get from Liquid-rendered metafields (no API call needed)
            updateProductReviews(template, productHandle, cleanProductUrl);
            updateProductSubtitle(template, productHandle, cleanProductUrl);
            
            console.log('🟢 ✅ Product [' + index + '] processing complete');
        }).fail(function(jqXHR, textStatus, errorThrown) {
            console.error('🟢 ❌ Error fetching product:', fullUrl);
            console.error('🟢 ❌ Error details:', textStatus, errorThrown);
            console.error('🟢 ❌ jqXHR:', jqXHR);
            utils.log('Error fetching product:', fullUrl, errorThrown);
            template.classList.add('hidden');
        });
    }

    // Render content blocks (top_content or bottom_content)
    function renderContentBlocks(contentBlocks, container) {
        if (!contentBlocks || !Array.isArray(contentBlocks) || contentBlocks.length === 0) {
            return;
        }
        
        contentBlocks.forEach(function(contentBlock) {
            var contentEl = null;
            
            if (contentBlock.content_type === 'text') {
                if (contentBlock.text_type === 'title') {
                    contentEl = document.createElement('h2');
                    contentEl.className = 'octane-section-title';
                    contentEl.style.cssText = 'font-size: 24px; font-weight: 600; color: #3B332F; margin: 20px 0 16px; text-align: left; width: 100%;';
                } else if (contentBlock.text_type === 'description') {
                    contentEl = document.createElement('p');
                    contentEl.className = 'octane-section-description';
                    contentEl.style.cssText = 'font-size: 16px; color: #666; margin: 0 0 16px; text-align: left; width: 100%;';
                } else {
                    contentEl = document.createElement('div');
                    contentEl.className = 'octane-section-content';
                    contentEl.style.cssText = 'font-size: 16px; color: #3B332F; margin: 0 0 16px; text-align: left; width: 100%;';
                }
                
                if (contentBlock.text) {
                    contentEl.innerText = contentBlock.text;
                    container.appendChild(contentEl);
                }
            } else if (contentBlock.content_type === 'html') {
                contentEl = document.createElement('div');
                contentEl.className = 'octane-section-html';
                contentEl.style.cssText = 'margin: 0 0 16px; width: 100%;';
                if (contentBlock.html) {
                    contentEl.innerHTML = contentBlock.html;
                    container.appendChild(contentEl);
                }
            }
        });
    }

    // Process a single section
    function processSection(section, sectionIndex, vacationAnswer, productCardTemplate, listToAppendTo, globalProductIndex) {
        console.log('🟡 ========== PROCESSING SECTION [' + sectionIndex + '] ==========');
        console.log('🟡 Section:', section);
        
        globalProductIndex = globalProductIndex || { value: 0 }; // Use object to pass by reference
        
        // Create section container
        var sectionContainer = document.createElement('div');
        sectionContainer.className = 'octane-section';
        sectionContainer.setAttribute('data-section-id', section.id || sectionIndex);
        sectionContainer.style.cssText = 'width: 100%; margin-bottom: 40px;';
        
        // Render top content
        if (section.top_content && Array.isArray(section.top_content)) {
            console.log('🟡 Rendering top content, blocks:', section.top_content.length);
            renderContentBlocks(section.top_content, sectionContainer);
        }
        
        // Create products container for this section
        var sectionProductsContainer = document.createElement('div');
        sectionProductsContainer.className = 'octane-section-products';
        sectionProductsContainer.style.cssText = 'display: flex; flex-wrap: wrap; width: 100%;';
        
        // Create single container for all products
        var productsContainer = document.createElement('div');
        productsContainer.className = 'products-container';
        productsContainer.style.cssText = 'display: flex; flex-wrap: wrap; width: 100%; gap: 10px;';
        
        // Process products in this section
        console.log('🟡 Section products:', section.products);
        console.log('🟡 Is products array?', Array.isArray(section.products));
        console.log('🟡 Products count:', section.products ? section.products.length : 0);
        
        if (section.products && Array.isArray(section.products)) {
            console.log('🟡 ✅ Processing', section.products.length, 'products in section [' + sectionIndex + ']');
            section.products.forEach(function(product, productIndex) {
                console.log('🟡 Processing product [' + productIndex + '] in section [' + sectionIndex + ']');
                var template = productCardTemplate.cloneNode(true);
                var currentIndex = globalProductIndex.value;
                
                processProduct(product, currentIndex, template, vacationAnswer, productsContainer);
                
                globalProductIndex.value++; // Increment global index
            });
        } else {
            console.log('🟡 ⚠️ No products array found in section [' + sectionIndex + ']');
        }
        
        // Append products container if it has content
        console.log('🟡 productsContainer children:', productsContainer.children.length);
        
        if (productsContainer.children.length > 0) {
            sectionProductsContainer.appendChild(productsContainer);
            console.log('🟡 ✅ productsContainer appended to sectionProductsContainer');
        }
        
        sectionContainer.appendChild(sectionProductsContainer);
        console.log('🟡 ✅ sectionProductsContainer appended to sectionContainer');
        
        // Render bottom content
        if (section.bottom_content && Array.isArray(section.bottom_content)) {
            console.log('🟡 Rendering bottom content, blocks:', section.bottom_content.length);
            renderContentBlocks(section.bottom_content, sectionContainer);
        }
        
        // Append section to main container
        console.log('🟡 listToAppendTo:', listToAppendTo);
        console.log('🟡 listToAppendTo parent:', listToAppendTo ? listToAppendTo.parentElement : null);
        
        if (listToAppendTo && listToAppendTo.parentElement) {
            listToAppendTo.parentElement.appendChild(sectionContainer);
            console.log('🟡 ✅ Section appended to listToAppendTo.parentElement');
        } else {
            var quizContainer = document.querySelector(".octane-ai-quiz-rendered");
            if (quizContainer) {
                quizContainer.appendChild(sectionContainer);
                console.log('🟡 ✅ Section appended to quizContainer');
            } else {
                console.error('🟡 ❌ No container found to append section to');
            }
        }
        
        console.log('🟡 ✅ Section [' + sectionIndex + '] processing complete. Global product index:', globalProductIndex.value);
        return globalProductIndex; // Return updated index
    }

    // Main handler function
    function handleQuizResults(e) {
        console.log('🔵 ========== QUIZ EVENT TRIGGERED ==========');
        console.log('🔵 Event Type:', e.type);
        console.log('🔵 Event Timestamp:', new Date().toISOString());
        
        utils.logSection('OCTANE AI QUIZ EVENT OBJECT', e);
        utils.logSection('EVENT DETAIL', e.detail);
        utils.logSection('EVENT DETAIL KEYS', Object.keys(e.detail || {}));
        
        // Log ALL properties of e.detail to see what's available
        console.log('🔵 Full e.detail object:', e.detail);
        if (e.detail) {
            console.log('🔵 e.detail.sections:', e.detail.sections);
            console.log('🔵 e.detail.product_blocks:', e.detail.product_blocks);
            console.log('🔵 e.detail.products:', e.detail.products);
            console.log('🔵 e.detail.recommended_products:', e.detail.recommended_products);
            console.log('🔵 e.detail.result:', e.detail.result);
            console.log('🔵 e.detail.results:', e.detail.results);
            console.log('🔵 e.detail.data:', e.detail.data);
            // Log every key in detail
            for (var key in e.detail) {
                console.log('🔵 e.detail[' + key + ']:', typeof e.detail[key], Array.isArray(e.detail[key]) ? 'Array[' + e.detail[key].length + ']' : e.detail[key]);
            }
        }
        
        // Extract quiz answers
        var quizAnswers = e.detail.answers || e.detail.quiz_data || {};
        var vacationAnswer = utils.extractQuizAnswer(quizAnswers, 'vacation');
        
        utils.logSection('QUIZ ANSWERS', quizAnswers);
        utils.log('Vacation answer found:', vacationAnswer);
        
        // Setup DOM elements
        console.log('🔵 Checking for DOM elements...');
        var list = document.querySelector(".octane-ai-quiz-rendered");
        if (!list) {
            console.error('❌ Quiz container (.octane-ai-quiz-rendered) not found');
            utils.log('Quiz container not found');
            return;
        }
        console.log('✅ Quiz container found:', list);
        
        var productCardTemplate = document.querySelector(".quiz-items-layout .recommended-product-card");
        if (!productCardTemplate) {
            console.error('❌ Product card template (.quiz-items-layout .recommended-product-card) not found');
            utils.log('Product card template not found');
            return;
        }
        console.log('✅ Product card template found:', productCardTemplate);
        
        list.classList.add('display_flex');
        
        var listToAppendTo = list.querySelector('[data-element*="oct-quiz-resultitem"]');
        console.log('🔵 listToAppendTo:', listToAppendTo);
        
        var quizContainer = document.querySelector(".quiz-wrapper");
        if (quizContainer) {
            quizContainer.classList.add('overlayed');
            console.log('✅ Quiz wrapper found and overlay added');
        } else {
            console.log('⚠️ Quiz wrapper not found');
        }
        
        // Check for sections first (new format)
        var sections = e.detail.sections || e.detail.product_blocks;
        console.log('🔵 Sections/product_blocks found:', sections);
        console.log('🔵 Is Array?', Array.isArray(sections));
        console.log('🔵 Length:', sections ? sections.length : 0);
        
        if (sections && Array.isArray(sections)) {
            console.log('✅ Sections/product_blocks is an array with', sections.length, 'items');
            
            // Check if first item has sections structure (has top_content, bottom_content, products)
            var isSectionsFormat = sections.length > 0 && 
                (sections[0].top_content !== undefined || sections[0].bottom_content !== undefined || 
                 (sections[0].products && Array.isArray(sections[0].products)));
            
            console.log('🔵 Is sections format?', isSectionsFormat);
            if (sections.length > 0) {
                console.log('🔵 First section structure:', {
                    hasTopContent: sections[0].top_content !== undefined,
                    hasBottomContent: sections[0].bottom_content !== undefined,
                    hasProducts: sections[0].products !== undefined,
                    productsIsArray: Array.isArray(sections[0].products),
                    productsLength: sections[0].products ? sections[0].products.length : 0,
                    firstSectionKeys: Object.keys(sections[0])
                });
            }
            
            if (isSectionsFormat) {
                console.log('✅ Processing sections format with top/bottom content');
                utils.log('Processing sections format with top/bottom content');
                utils.log('Sections:', sections);
                
                // Log each section's products
                sections.forEach(function(section, idx) {
                    console.log('🔵 Section [' + idx + ']:', {
                        id: section.id,
                        productsCount: section.products ? section.products.length : 0,
                        products: section.products,
                        topContent: section.top_content,
                        bottomContent: section.bottom_content
                    });
                });
                
                // Set AJAX to synchronous to maintain product order
                jQuery.ajaxSetup({ async: false });
                
                // Track global product index across all sections
                var globalProductIndex = { value: 0 };
                
                // Process each section
                sections.forEach(function(section, sectionIndex) {
                    console.log('🔵 Processing section [' + sectionIndex + ']...');
                    utils.log('Processing section', sectionIndex, section);
                    globalProductIndex = processSection(section, sectionIndex, vacationAnswer, productCardTemplate, listToAppendTo, globalProductIndex);
                    console.log('🔵 Section [' + sectionIndex + '] processed. Global product index:', globalProductIndex.value);
                });
                
                // Reset async setting
                jQuery.ajaxSetup({ async: true });
            } else {
                // Old format: product_blocks is just an array of products
                console.log('✅ Processing old product_blocks format');
                utils.log('Processing old product_blocks format');
                
                // Flatten product blocks array
                var products = [].concat.apply([], sections);
                console.log('🔵 Flattened products array:', products);
                console.log('🔵 Products count:', products.length);
                
                if (products.length === 0) {
                    console.error('❌ No products found after flattening');
                    utils.log('No products found');
                    if (quizContainer) quizContainer.classList.remove('overlayed');
                    return;
                }
                
                // Log each product
                products.forEach(function(product, idx) {
                    console.log('🔵 Product [' + idx + ']:', {
                        title: product.title,
                        link: product.link,
                        variant_id: product.variant_id,
                        variantId: product.variantId,
                        scent: product.scent
                    });
                });
                
                var productsContainer = document.createElement("div");
                productsContainer.classList.add('products-container');
                productsContainer.style.cssText = 'display: flex; flex-wrap: wrap; width: 100%; gap: 10px;';
                
                if (listToAppendTo && listToAppendTo.parentElement) {
                    listToAppendTo.parentElement.appendChild(productsContainer);
                    console.log('✅ Product container appended to DOM');
                } else {
                    console.log('⚠️ listToAppendTo or parentElement not found, appending to quiz container');
                    list.appendChild(productsContainer);
                }
                
                // Set AJAX to synchronous to maintain product order
                jQuery.ajaxSetup({ async: false });
                
                // Process each product
                products.forEach(function(product, index) {
                    console.log('🔵 Processing product [' + index + ']...');
                    var template = productCardTemplate.cloneNode(true);
                    processProduct(product, index, template, vacationAnswer, productsContainer);
                });
                
                // Reset async setting
                jQuery.ajaxSetup({ async: true });
            }
        } else {
            console.error('❌ No sections or product_blocks found');
            console.error('❌ sections value:', sections);
            console.error('❌ sections type:', typeof sections);
            utils.log('No sections or product_blocks found');
            if (quizContainer) quizContainer.classList.remove('overlayed');
            return;
        }
        
        // Remove overlay when done
        if (quizContainer) {
            quizContainer.classList.remove('overlayed');
            console.log('✅ Overlay removed');
        }
        
        console.log('🔵 ========== QUIZ EVENT PROCESSING COMPLETE ==========');
    }

    // Handle variant selection change
    function handleVariantChange(event) {
        var selectVariant = event.target;
        var productCardContainer = selectVariant.closest('.recommended-product-card');
        if (!productCardContainer) return;
        
        var selectedOption = selectVariant.options[selectVariant.selectedIndex];
        var varImg = selectedOption.getAttribute('data-varimg');
        var variantId = selectVariant.value;
        
        // Update image
        var img = productCardContainer.querySelector('.image__container img');
        if (img && varImg) {
            img.src = varImg;
        }
        
        // Update add to cart link
        var addToCartBtn = productCardContainer.querySelector('.upsell-addtocart');
        if (addToCartBtn) {
            var linkVariant = CONFIG.cartAddUrl + "?id=" + variantId + "&quantity=1";
            addToCartBtn.setAttribute("href", linkVariant);
            addToCartBtn.setAttribute("data-variant", variantId);
        }
        
        // Update price
        var variantPrice = selectedOption.getAttribute('data-variant-price');
        var variantComparePrice = selectedOption.getAttribute('data-variant-compare-price');
        var priceEl = productCardContainer.querySelector('.price--end .money');
        var priceWithoutDiscount = productCardContainer.querySelector('.price--without-discount');
        var priceWithoutDiscountMoney = priceWithoutDiscount ? priceWithoutDiscount.querySelector('.money') : null;
        
        if (priceEl && variantPrice) {
            priceEl.innerText = "$" + parseFloat(variantPrice).toFixed(2);
            
            // Update aria-label based on whether there's a discount
            if (variantComparePrice) {
                priceEl.setAttribute("aria-label", "Discounted price");
                
                // Show and set original price
                if (priceWithoutDiscount && priceWithoutDiscountMoney) {
                    priceWithoutDiscountMoney.innerText = "$" + parseFloat(variantComparePrice).toFixed(2);
                    priceWithoutDiscount.style.display = "inline";
                }
            } else {
                priceEl.setAttribute("aria-label", "Price");
                
                // Hide original price
                if (priceWithoutDiscount) {
                    priceWithoutDiscount.style.display = "none";
                }
            }
        }
    }

    // Handle subscription selection change
    function handleSubscriptionChange(event) {
        var selectSubscr = event.target;
        var productCardContainer = selectSubscr.closest('.recommended-product-card');
        if (!productCardContainer) return;
        
        var addToCartBtn = productCardContainer.querySelector('.upsell-addtocart');
        if (!addToCartBtn) return;
        
        if (event.target.value > 2) {
            addToCartBtn.setAttribute("data-sp-id", event.target.value);
            var frequencyText = event.target.options[event.target.selectedIndex].innerText.replace("Save 20% - ", "Delivered every");
            addToCartBtn.setAttribute("data-frequency", frequencyText);
        } else {
            addToCartBtn.removeAttribute("data-sp-id");
            addToCartBtn.removeAttribute("data-frequency");
        }
    }

    // Initialize event listeners
    function init() {
        console.log('🟣 ========== INITIALIZING QUIZ SCRIPTS ==========');
        console.log('🟣 Document ready state:', document.readyState);
        console.log('🟣 jQuery available?', typeof jQuery !== 'undefined');
        
        // Use event delegation for better performance
        document.addEventListener('change', function(event) {
            if (event.target.classList.contains('upsells_variants')) {
                handleVariantChange(event);
            } else if (event.target.classList.contains('subscriptions')) {
                handleSubscriptionChange(event);
            }
        });
        console.log('🟣 ✅ Change event listener registered');
        
        // Listen for Octane AI quiz events
        document.addEventListener('octane.quiz.accessed', function(e) {
            console.log('🟣 📢 octane.quiz.accessed event received!');
            handleQuizResults(e);
        }, false);
        
        document.addEventListener('octane.quiz.completed', function(e) {
            console.log('🟣 📢 octane.quiz.completed event received!');
            handleQuizResults(e);
        }, false);
        
        console.log('🟣 ✅ Octane AI event listeners registered');
        console.log('🟣 Listening for: octane.quiz.accessed, octane.quiz.completed');
        
        // Also listen for any custom events that might be used
        document.addEventListener('octane.quiz.result', function(e) {
            console.log('🟣 📢 octane.quiz.result event received!');
            handleQuizResults(e);
        }, false);
        
        console.log('🟣 ========== INITIALIZATION COMPLETE ==========');
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        console.log('🟣 DOM is loading, waiting for DOMContentLoaded...');
        document.addEventListener('DOMContentLoaded', function() {
            console.log('🟣 DOMContentLoaded fired, initializing...');
            init();
        });
    } else {
        console.log('🟣 DOM already ready, initializing immediately...');
        init();
    }

})();
